import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CircularProgress } from "@mui/material";
import { Play, Minus, Plus, Type, Mic2 } from "lucide-react";

const MiddleCompCr = ({
  selectedTextbook,
  socketres,
  stories = [],
  currentStoryIndex,
  selectedStudent,
  students,
  wsRef,
  isRecordClicked,
  activeWordIndex,
  sessionId,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [networkSpeed, setNetworkSpeed] = useState("high");
  const [choosenstudent, setchoosenstudent] = useState();
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const [playingWordIndex, setPlayingWordIndex] = useState(null);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [showHighlights, setShowHighlights] = useState(true);
  const [hasRecorded, setHasRecorded] = useState(false);
  const [fontSize, setFontSize] = useState(42); // Further reduced to 42px
  const timeoutRef = useRef();
  const audioRef = useRef(null);
  const [audioInitialized, setAudioInitialized] = useState(false);
  const [wsReady, setWsReady] = useState(false);
  const [isReady, setIsReady] = useState(false);

  //update fontsize based on passage length
  useEffect(() => {
    const content = stories?.length
      ? stories[currentStoryIndex]?.content
      : selectedTextbook?.content;

    const contentLength = content?.length || 0;

    let calculatedFontSize = 34;

    if (contentLength > 500) {
      calculatedFontSize = 20;
    } else if (contentLength > 420) {
      calculatedFontSize = 22;
    } else if (contentLength > 340) {
      calculatedFontSize = 24;
    } else if (contentLength > 270) {
      calculatedFontSize = 26;
    } else if (contentLength > 210) {
      calculatedFontSize = 28;
    } else if (contentLength > 160) {
      calculatedFontSize = 30;
    }

    setFontSize(calculatedFontSize);
  }, [stories, currentStoryIndex, selectedTextbook]);

  // Initialize audio element
  useEffect(() => {
    const initAudio = () => {
      try {
        if (!audioRef.current) {
          audioRef.current = new Audio();
          audioRef.current.addEventListener("error", (e) => {
            console.error("Audio error:", e.target.error);
          });
          audioRef.current.addEventListener("ended", () => {
            console.log("Audio playback ended");
            setPlayingWordIndex(null);
          });
          setAudioInitialized(true);
          console.log("Audio initialized successfully");
        }
      } catch (error) {
        console.error("Error initializing audio:", error);
      }
    };

    initAudio();

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
        audioRef.current.removeEventListener("error", () => {});
        audioRef.current.removeEventListener("ended", () => {});
        audioRef.current = null;
      }
      setAudioInitialized(false);
    };
  }, []);

  // Network speed detection
  useEffect(() => {
    const checkNetworkSpeed = () => {
      if ("connection" in navigator) {
        const connection = navigator.connection;
        const updateNetworkSpeed = () => {
          // More granular speed detection
          if (connection.effectiveType === "4g" && connection.downlink >= 10) {
            setNetworkSpeed("high");
          } else if (
            connection.effectiveType === "4g" ||
            connection.effectiveType === "3g"
          ) {
            setNetworkSpeed("medium");
          } else {
            setNetworkSpeed("low");
          }
          console.log(
            "Network speed updated:",
            connection.effectiveType,
            "Downlink:",
            connection.downlink,
          );
        };
        updateNetworkSpeed();
        connection.addEventListener("change", updateNetworkSpeed);
        return () =>
          connection.removeEventListener("change", updateNetworkSpeed);
      } else {
        // Fallback for browsers that don't support Network Information API
        const img = new Image();
        const startTime = Date.now();
        img.onload = () => {
          const loadTime = Date.now() - startTime;
          if (loadTime < 1000) setNetworkSpeed("high");
          else if (loadTime < 3000) setNetworkSpeed("medium");
          else setNetworkSpeed("low");
        };
        img.src = "https://www.google.com/images/phd/px.gif"; // Small test image
      }
    };
    checkNetworkSpeed();
  }, []);

  // Student selection and animation effect
  useEffect(() => {
    const selectedStudentData = students.find(
      (student) => student.id === selectedStudent,
    );
    setchoosenstudent(selectedStudentData);
    setCurrentWordIndex(-1);
    setAnimationComplete(false);
    setHasRecorded(false);

    if (!selectedStudentData?.has_submitted) return;

    const currentContent = stories?.length
      ? stories[currentStoryIndex]
      : selectedTextbook;

    const content = currentContent?.content;
    if (!content) return;

    // Process the words based on the content
    const words = content.split(" ");
    let currentIndex = 0;
    const wordCount = words.length;
    const animationSpeed = wordCount > 200 ? 25 : wordCount > 100 ? 35 : 45;

    const processNextWord = () => {
      if (currentIndex >= words.length) {
        setAnimationComplete(true);
        setHasRecorded(true);
        return;
      }
      setCurrentWordIndex(currentIndex);
      currentIndex++;
      timeoutRef.current = setTimeout(processNextWord, animationSpeed);
    };

    timeoutRef.current = setTimeout(processNextWord, 300);

    return () => {
      clearTimeout(timeoutRef.current);
      setCurrentWordIndex(-1);
      setAnimationComplete(false);
    };
  }, [selectedStudent, students, currentStoryIndex, stories, selectedTextbook]);

  // Check WebSocket connection status safely
  useEffect(() => {
    // Helper function to safely check websocket state
    const checkConnection = () => {
      // Prevent crashes if websocket is destroyed/unmounted
      if (!wsRef?.current) {
        setWsReady(false);
        return;
      }

      // Check whether websocket connection is open
      const isReady = wsRef.current.readyState === WebSocket.OPEN;

      setWsReady(isReady);

      console.log("WebSocket ready state:", isReady);
    };

    // Run initial websocket status check
    checkConnection();

    // Store stable websocket reference
    // Important during unmount/cleanup to avoid null errors
    const socket = wsRef?.current;

    // Exit early if websocket does not exist
    if (!socket) return;

    // Handle websocket state updates
    const handleStateChange = () => {
      checkConnection();
    };

    // Listen for websocket lifecycle events
    socket.addEventListener("open", handleStateChange);
    socket.addEventListener("close", handleStateChange);
    socket.addEventListener("error", handleStateChange);

    // Cleanup listeners on component unmount
    return () => {
      socket.removeEventListener("open", handleStateChange);
      socket.removeEventListener("close", handleStateChange);
      socket.removeEventListener("error", handleStateChange);
    };
  }, [wsRef]);

  // Check if everything is ready
  useEffect(() => {
    const isSystemReady =
      wsReady && audioInitialized && stories[currentStoryIndex]?.id;
    setIsReady(isSystemReady);

    if (isSystemReady) {
      console.log("System ready:", {
        wsReady,
        audioInitialized,
        storyId: stories[currentStoryIndex]?.id,
      });
    } else {
      console.log("System not ready:", {
        wsReady,
        audioInitialized,
        storyId: stories[currentStoryIndex]?.id,
      });
    }
  }, [wsReady, audioInitialized, stories, currentStoryIndex]);

  // Update handleWordClick to use the WebSocket context
  const handleWordClick = (index) => {
    try {
      const words = stories?.length
        ? stories[currentStoryIndex]?.content.split(" ")
        : selectedTextbook?.content.split(" ");

      if (!words || !words[index]) return;

      setPlayingWordIndex(index);

      // Check if WebSocket is connected directly using wsRef
      const isWsConnected =
        wsRef?.current && wsRef.current.readyState === WebSocket.OPEN;

      // Try WebSocket if connected
      if (isWsConnected && audioInitialized && stories[currentStoryIndex]?.id) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;

        const message = {
          type: "word_audio_request",
          index: index,
          session_id: sessionId || null,
          student_id: selectedStudent || null,
          reading_content_id: stories[currentStoryIndex].id,
        };

        console.log("Sending audio request:", message);
        wsRef.current.send(JSON.stringify(message));
      }
      // Fallback to browser's speech synthesis
      else {
        const word = words[index];
        if ("speechSynthesis" in window) {
          const utterance = new SpeechSynthesisUtterance(word);
          utterance.rate = 0.9;
          utterance.onend = () => setPlayingWordIndex(null);
          speechSynthesis.speak(utterance);
        } else {
          console.log("Speech synthesis not supported");
          setTimeout(() => setPlayingWordIndex(null), 500);
        }
      }
    } catch (error) {
      console.error("Error in handleWordClick:", error);
      setPlayingWordIndex(null);
    }
  };

  // WebSocket message handler
  useEffect(() => {
    if (!wsRef?.current || !audioInitialized) return;

    const handleWebSocketMessage = async (event) => {
      try {
        const response = JSON.parse(event.data);
        console.log("Received WebSocket message:", response);

        if (response?.type && response?.audio) {
          const audio = audioRef.current;
          audio.src = response.audio;

          try {
            await audio.play();
            console.log("Audio playback started successfully");
          } catch (error) {
            console.error("Error playing audio:", error);
            setPlayingWordIndex(null);
          }
        }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    wsRef.current.addEventListener("message", handleWebSocketMessage);
    return () => {
      if (wsRef?.current) {
        wsRef.current.removeEventListener("message", handleWebSocketMessage);
      }
    };
  }, [wsRef, audioInitialized]);

  // Socket response handler
  useEffect(() => {
    if (socketres?.audio_url && audioInitialized) {
      console.log("Received socket response:", socketres);
      const audio = audioRef.current;
      audio.src = socketres.audio_url;

      audio.play().catch((error) => {
        console.error("Error playing audio from socketres:", error);
        setPlayingWordIndex(null);
      });
    }
  }, [socketres, audioInitialized]);

  // Modernized renderContentWithHighlights
  const renderContentWithHighlights = () => {
    const content = stories?.length
      ? stories[currentStoryIndex]?.content
      : selectedTextbook?.content;
    if (!content) return null;

    const words = content.split(" ");

    const socketMisspelled =
      socketres?.submitted_assignment?.student === selectedStudent
        ? socketres.submitted_assignment.misspelled_words
        : socketres?.student === selectedStudent
          ? socketres.misspelled_words
          : null;

    const misspelledWords =
      socketMisspelled || choosenstudent?.misspelled_words || [];
    const misspelledIndices = new Set();

    if (misspelledWords.length > 0) {
      misspelledWords.forEach((mw) => {
        if (typeof mw === "object" && mw !== null) {
          if (typeof mw.index === "number") {
            misspelledIndices.add(mw.index);
          } else if (mw.word) {
            const misspelledWord = mw.word.toLowerCase().trim();
            for (let index = 0; index < words.length; index++) {
              const word = words[index].toLowerCase().replace(/[^\w]/g, "");
              if (word === misspelledWord.replace(/[^\w]/g, "")) {
                misspelledIndices.add(index);
                break;
              }
            }
          }
        }
      });
    }

    return (
      <div
        className="font-extrabold text-slate-800 leading-[1.3] tracking-tight transition-all duration-300"
        style={{ fontSize: `${fontSize}px` }}
      >
        {words.map((word, index) => {
          const isHighlighted = misspelledIndices.has(index);
          const isPlaying = playingWordIndex === index;
          const isTranscriptActive = activeWordIndex === index;

          return (
            <motion.span
              key={`word-${index}`}
              className="relative inline-block mr-1 mb-1 cursor-pointer group"
              onClick={() => handleWordClick(index)}
              whileTap={{ scale: 1.15 }}
              whileHover={{ scale: 1.05 }}
            >
              <span
                className={`relative z-10 px-2.5 py-1 transition-all duration-300 rounded-2xl ${
                  isTranscriptActive
                    ? "bg-green-200 text-green-900 shadow-sm"
                    : isPlaying
                      ? "bg-yellow-400/90 text-blue-600 shadow-sm"
                      : isHighlighted
                        ? "bg-red-500 text-white"
                        : "hover:bg-[#FDF2E9]/80 hover:text-blue-600"
                }`}
                style={{ fontFamily: "'Noto Serif', serif" }}
              >
                {word}
              </span>
            </motion.span>
          );
        })}
      </div>
    );
  };

  // Image handling with improved resolution selection
  const getImageUrl = () => {
    if (!stories?.length || !stories[currentStoryIndex]?.images?.length)
      return null;
    const image = stories[currentStoryIndex].images[0];

    switch (networkSpeed) {
      case "high":
        return image.high_resolution || image.medium_resolution;
      case "medium":
        return image.medium_resolution || image.low_resolution;
      case "low":
        return image.low_resolution || image.medium_resolution;
      default:
        return image.medium_resolution;
    }
  };

  useEffect(() => {
    setImageLoaded(false);
  }, [currentStoryIndex, stories]);

  const renderImage = () => {
    const imageUrl = getImageUrl();
    if (!imageUrl) return null;

    return (
      <img
        src={imageUrl}
        alt={`Story ${currentStoryIndex + 1}`}
        className={`w-full h-full object-cover transition-all duration-700 ${
          !imageLoaded
            ? "opacity-0 scale-95 blur-sm"
            : "opacity-100 scale-100 blur-0"
        }`}
        onLoad={() => setImageLoaded(true)}
      />
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-[2.5rem] p-4 shadow-[0_20px_40px_-12px_rgba(0,0,0,0.03)] border border-slate-50 flex flex-col lg:flex-row gap-4 items-stretch w-full"
    >
      {/* Left Column: Vertically Centered Text Content (52%) */}
      <div className="order-2 lg:order-1 w-full lg:w-[52%] flex flex-col items-start justify-center text-left">
        {/* Passage Text Area - Centered Vertically and Horizontally */}
        <div
          className="w-full overflow-y-auto max-h-[420px] custom-scrollbar px-2"
          style={{ fontFamily: "'Noto Serif', serif" }}
        >
          {renderContentWithHighlights()}
        </div>
      </div>

      {/* Right Column: Premium Visual Container (48%) */}
      {stories?.length > 0 && (
        <div className="order-1 lg:order-2 w-full lg:w-[48%] flex justify-center items-center">
          {/* Fixed h-[320px], same in row mode and stacked mode. No
      aspect-ratio, no h-full/flex-stretch. Those depend on the row's
      own height (indefinite when stacked) or on the box's width
      (which differs hugely between the 48%-wide row column and the
      full-width stacked column) — both are what caused the box to
      collapse or balloon depending on layout mode. A flat px height
      sidesteps all of that: it's identical regardless of zoom,
      breakpoint, or the source image's own aspect ratio. */}
          <div className="w-full h-[320px] bg-[#F8FAFC] rounded-[2.5rem] border border-slate-100/50 relative overflow-hidden shadow-sm">
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <CircularProgress
                  size={40}
                  className="text-blue-400/20"
                  thickness={4}
                />
              </div>
            )}

            {/* w-full h-full is safe again here because the parent now has an
        explicit px height (not a percentage/stretch-derived one), so
        this percentage resolves normally regardless of flex context. */}
            <div className="relative z-10 w-full h-full">{renderImage()}</div>

            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-400/5 to-transparent rounded-full -mr-16 -mt-16 pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-orange-400/5 to-transparent rounded-full -ml-12 -mb-12 pointer-events-none" />
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default MiddleCompCr;
// ...end of file...
